import { useState } from 'react';
import { 
  X, Download, Upload, Globe, Smartphone, Server, 
  DollarSign, CheckCircle, AlertCircle, ExternalLink,
  Code, FileCode, Package, Rocket, HelpCircle,
  Apple, Play, Cloud, Database, ChevronDown, ChevronUp,
  Copy, Check, BookOpen, Zap, Shield, Users
} from 'lucide-react';
import { toast } from 'sonner';
import { useLanguage } from '../context/LanguageContext';

const ServicesInfo = ({ isVisible, onClose }) => {
  const { language } = useLanguage();
  const [activeTab, setActiveTab] = useState('services');
  const [expandedGuide, setExpandedGuide] = useState(null);
  const [copiedText, setCopiedText] = useState(null);
  
  const isSpanish = language === 'es';
  
  const copyToClipboard = (text, id) => {
    navigator.clipboard.writeText(text);
    setCopiedText(id);
    toast.success(isSpanish ? 'Copiado al portapapeles' : 'Copied to clipboard');
    setTimeout(() => setCopiedText(null), 2000);
  };
  
  if (!isVisible) return null;
  
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
      <div className="bg-zinc-900 border border-zinc-700 rounded-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col mx-4">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-zinc-700 bg-gradient-to-r from-indigo-600/20 to-purple-600/20">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-indigo-600 rounded-xl flex items-center justify-center">
              <Rocket className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-zinc-100">
                {isSpanish ? 'Servicios de DamyxIA' : 'DamyxIA Services'}
              </h2>
              <p className="text-sm text-zinc-400">
                {isSpanish ? 'Todo lo que necesitas saber para publicar tu proyecto' : 'Everything you need to know to publish your project'}
              </p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-zinc-800 rounded-lg">
            <X className="w-5 h-5 text-zinc-400" />
          </button>
        </div>
        
        {/* Tabs */}
        <div className="flex border-b border-zinc-700">
          <button
            onClick={() => setActiveTab('services')}
            className={`flex-1 px-4 py-3 text-sm font-medium flex items-center justify-center gap-2 ${
              activeTab === 'services' 
                ? 'text-indigo-400 border-b-2 border-indigo-500 bg-indigo-500/10' 
                : 'text-zinc-400 hover:text-zinc-200'
            }`}
          >
            <Zap className="w-4 h-4" />
            {isSpanish ? 'Servicios' : 'Services'}
          </button>
          <button
            onClick={() => setActiveTab('costs')}
            className={`flex-1 px-4 py-3 text-sm font-medium flex items-center justify-center gap-2 ${
              activeTab === 'costs' 
                ? 'text-indigo-400 border-b-2 border-indigo-500 bg-indigo-500/10' 
                : 'text-zinc-400 hover:text-zinc-200'
            }`}
          >
            <DollarSign className="w-4 h-4" />
            {isSpanish ? '¿Quién Paga Qué?' : 'Who Pays What?'}
          </button>
          <button
            onClick={() => setActiveTab('guides')}
            className={`flex-1 px-4 py-3 text-sm font-medium flex items-center justify-center gap-2 ${
              activeTab === 'guides' 
                ? 'text-indigo-400 border-b-2 border-indigo-500 bg-indigo-500/10' 
                : 'text-zinc-400 hover:text-zinc-200'
            }`}
          >
            <BookOpen className="w-4 h-4" />
            {isSpanish ? 'Guías de Publicación' : 'Publishing Guides'}
          </button>
        </div>
        
        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          
          {/* Services Tab */}
          {activeTab === 'services' && (
            <div className="space-y-6">
              {/* What DamyxIA Does */}
              <div className="bg-gradient-to-br from-indigo-600/10 to-purple-600/10 border border-indigo-500/30 rounded-xl p-6">
                <h3 className="text-lg font-semibold text-indigo-400 mb-4 flex items-center gap-2">
                  <CheckCircle className="w-5 h-5" />
                  {isSpanish ? '✅ Lo que DamyxIA HACE por ti:' : '✅ What DamyxIA DOES for you:'}
                </h3>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="flex items-start gap-3">
                    <Code className="w-5 h-5 text-green-400 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-zinc-200 font-medium">{isSpanish ? 'Genera código completo' : 'Generates complete code'}</p>
                      <p className="text-zinc-400 text-sm">HTML, CSS, JavaScript, React, Python, Node.js, etc.</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <FileCode className="w-5 h-5 text-green-400 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-zinc-200 font-medium">{isSpanish ? 'Crea estructura del proyecto' : 'Creates project structure'}</p>
                      <p className="text-zinc-400 text-sm">{isSpanish ? 'Carpetas, archivos, configuración' : 'Folders, files, configuration'}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <Shield className="w-5 h-5 text-green-400 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-zinc-200 font-medium">{isSpanish ? 'Revisa y corrige errores' : 'Reviews and fixes errors'}</p>
                      <p className="text-zinc-400 text-sm">{isSpanish ? 'Análisis de calidad automático' : 'Automatic quality analysis'}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <Package className="w-5 h-5 text-green-400 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-zinc-200 font-medium">{isSpanish ? 'Archivos de despliegue' : 'Deployment files'}</p>
                      <p className="text-zinc-400 text-sm">Dockerfile, vercel.json, netlify.toml</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <Smartphone className="w-5 h-5 text-green-400 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-zinc-200 font-medium">{isSpanish ? 'Apps móviles' : 'Mobile apps'}</p>
                      <p className="text-zinc-400 text-sm">React Native, Flutter</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <Users className="w-5 h-5 text-green-400 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-zinc-200 font-medium">{isSpanish ? 'Colaboración en grupos' : 'Group collaboration'}</p>
                      <p className="text-zinc-400 text-sm">{isSpanish ? 'Trabaja en equipo' : 'Work as a team'}</p>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Free Option */}
              <div className="bg-gradient-to-br from-green-600/10 to-emerald-600/10 border border-green-500/30 rounded-xl p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-green-600 rounded-full flex items-center justify-center">
                    <Download className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-green-400">
                      💡 {isSpanish ? 'Opción GRATIS' : 'FREE Option'}
                    </h3>
                    <p className="text-zinc-400 text-sm">{isSpanish ? 'Lo que puedes hacer sin costo adicional' : 'What you can do at no extra cost'}</p>
                  </div>
                </div>
                
                <div className="bg-zinc-900/50 rounded-lg p-4 space-y-3">
                  <div className="flex items-center gap-2 text-zinc-200">
                    <span className="text-green-400 font-bold">1.</span>
                    {isSpanish 
                      ? 'El usuario descarga el código y se encarga de todo.'
                      : 'The user downloads the code and handles everything.'}
                  </div>
                  
                  <div className="pl-6 space-y-2 text-sm text-zinc-400">
                    <p>• {isSpanish ? 'Descargar código fuente completo (.zip)' : 'Download complete source code (.zip)'}</p>
                    <p>• {isSpanish ? 'Incluye todos los archivos necesarios' : 'Includes all necessary files'}</p>
                    <p>• {isSpanish ? 'Documentación básica incluida' : 'Basic documentation included'}</p>
                    <p>• {isSpanish ? 'Tú eliges dónde y cómo publicar' : 'You choose where and how to publish'}</p>
                    <p>• {isSpanish ? 'Tú te quedas con el 100% de las ganancias' : 'You keep 100% of the profits'}</p>
                  </div>
                </div>
              </div>
              
              {/* Model Diagram */}
              <div className="bg-zinc-800/50 rounded-xl p-6">
                <h3 className="text-lg font-semibold text-zinc-200 mb-4 flex items-center gap-2">
                  🎯 {isSpanish ? 'Cómo funciona el modelo:' : 'How the model works:'}
                </h3>
                
                <div className="space-y-4">
                  {/* DamyxIA Box */}
                  <div className="bg-indigo-600/20 border border-indigo-500/50 rounded-xl p-4">
                    <h4 className="font-semibold text-indigo-400 mb-3">DAMYXIA ({isSpanish ? 'Tu plataforma' : 'Your platform'})</h4>
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <p className="text-green-400">✅ {isSpanish ? 'Genera código' : 'Generates code'}</p>
                      <p className="text-green-400">✅ {isSpanish ? 'Revisa proyectos' : 'Reviews projects'}</p>
                      <p className="text-green-400">✅ {isSpanish ? 'Corrige errores' : 'Fixes errors'}</p>
                      <p className="text-green-400">✅ {isSpanish ? 'Aprueba para publicación' : 'Approves for publication'}</p>
                      <p className="text-red-400 col-span-2">❌ {isSpanish ? 'NO paga hosting ni tiendas de apps' : 'Does NOT pay for hosting or app stores'}</p>
                    </div>
                  </div>
                  
                  {/* Arrow */}
                  <div className="flex justify-center">
                    <div className="w-0 h-0 border-l-8 border-r-8 border-t-8 border-l-transparent border-r-transparent border-t-indigo-500"></div>
                  </div>
                  
                  {/* User Box */}
                  <div className="bg-purple-600/20 border border-purple-500/50 rounded-xl p-4">
                    <h4 className="font-semibold text-purple-400 mb-3">USUARIO</h4>
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <p className="text-green-400">✅ {isSpanish ? 'Crea su proyecto con DamyxIA' : 'Creates project with DamyxIA'}</p>
                      <p className="text-green-400">✅ {isSpanish ? 'Descarga su código' : 'Downloads code'}</p>
                      <p className="text-green-400">✅ {isSpanish ? 'Paga su propia cuenta de desarrollador' : 'Pays own developer account'}</p>
                      <p className="text-green-400">✅ {isSpanish ? 'Publica en las tiendas que quiera' : 'Publishes to stores of choice'}</p>
                      <p className="text-yellow-400 col-span-2">⭐ {isSpanish ? 'Se queda con el 100% de las ganancias de su app' : 'Keeps 100% of app profits'}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
          
          {/* Costs Tab */}
          {activeTab === 'costs' && (
            <div className="space-y-6">
              <div className="bg-gradient-to-r from-yellow-600/10 to-orange-600/10 border border-yellow-500/30 rounded-xl p-6">
                <h3 className="text-xl font-bold text-yellow-400 mb-4 flex items-center gap-2">
                  <DollarSign className="w-6 h-6" />
                  {isSpanish ? '¿Quién paga qué?' : 'Who pays what?'}
                </h3>
                <p className="text-zinc-300 mb-4">
                  {isSpanish 
                    ? 'Es importante entender que cada usuario es DUEÑO de su proyecto. Es su trabajo, su app, su negocio.'
                    : 'It\'s important to understand that each user OWNS their project. It\'s their work, their app, their business.'}
                </p>
              </div>
              
              {/* Cost Table */}
              <div className="bg-zinc-800/50 rounded-xl overflow-hidden">
                <table className="w-full">
                  <thead className="bg-zinc-700/50">
                    <tr>
                      <th className="px-4 py-3 text-left text-zinc-300 font-medium">{isSpanish ? 'Servicio' : 'Service'}</th>
                      <th className="px-4 py-3 text-left text-zinc-300 font-medium">{isSpanish ? 'Costo' : 'Cost'}</th>
                      <th className="px-4 py-3 text-left text-zinc-300 font-medium">{isSpanish ? '¿Quién paga?' : 'Who pays?'}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-zinc-700">
                    <tr className="hover:bg-zinc-800/50">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <Play className="w-5 h-5 text-green-500" />
                          <span className="text-zinc-200">Google Play Store</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-zinc-300">$25 USD <span className="text-zinc-500">({isSpanish ? 'una vez' : 'one time'})</span></td>
                      <td className="px-4 py-3">
                        <span className="px-2 py-1 bg-purple-600/20 text-purple-400 rounded text-sm">{isSpanish ? 'El Usuario' : 'The User'}</span>
                      </td>
                    </tr>
                    <tr className="hover:bg-zinc-800/50">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <Apple className="w-5 h-5 text-zinc-300" />
                          <span className="text-zinc-200">Apple App Store</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-zinc-300">$86 USD <span className="text-zinc-500">/{isSpanish ? 'año' : 'year'}</span></td>
                      <td className="px-4 py-3">
                        <span className="px-2 py-1 bg-purple-600/20 text-purple-400 rounded text-sm">{isSpanish ? 'El Usuario' : 'The User'}</span>
                      </td>
                    </tr>
                    <tr className="hover:bg-zinc-800/50">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <Cloud className="w-5 h-5 text-blue-400" />
                          <span className="text-zinc-200">Hosting Web (AWS, Vercel, etc.)</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-zinc-300">$0-50+ USD <span className="text-zinc-500">/{isSpanish ? 'mes' : 'month'}</span></td>
                      <td className="px-4 py-3">
                        <span className="px-2 py-1 bg-purple-600/20 text-purple-400 rounded text-sm">{isSpanish ? 'El Usuario' : 'The User'}</span>
                      </td>
                    </tr>
                    <tr className="hover:bg-zinc-800/50">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <Database className="w-5 h-5 text-orange-400" />
                          <span className="text-zinc-200">{isSpanish ? 'Base de datos' : 'Database'}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-zinc-300">$0-20+ USD <span className="text-zinc-500">/{isSpanish ? 'mes' : 'month'}</span></td>
                      <td className="px-4 py-3">
                        <span className="px-2 py-1 bg-purple-600/20 text-purple-400 rounded text-sm">{isSpanish ? 'El Usuario' : 'The User'}</span>
                      </td>
                    </tr>
                    <tr className="hover:bg-zinc-800/50">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <Server className="w-5 h-5 text-indigo-400" />
                          <span className="text-zinc-200">{isSpanish ? 'Dominio personalizado' : 'Custom domain'}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-zinc-300">$10-15 USD <span className="text-zinc-500">/{isSpanish ? 'año' : 'year'}</span></td>
                      <td className="px-4 py-3">
                        <span className="px-2 py-1 bg-purple-600/20 text-purple-400 rounded text-sm">{isSpanish ? 'El Usuario' : 'The User'}</span>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
              
              {/* Why User Pays */}
              <div className="bg-zinc-800/50 rounded-xl p-6">
                <h4 className="font-semibold text-zinc-200 mb-4 flex items-center gap-2">
                  <HelpCircle className="w-5 h-5 text-indigo-400" />
                  {isSpanish ? '¿Por qué DamyxIA NO paga estos costos?' : 'Why doesn\'t DamyxIA pay these costs?'}
                </h4>
                <div className="space-y-3 text-zinc-400">
                  <p className="flex items-start gap-2">
                    <span className="text-yellow-400">1.</span>
                    <span><strong className="text-zinc-200">{isSpanish ? 'Cada usuario es DUEÑO de su proyecto' : 'Each user OWNS their project'}</strong> - {isSpanish ? 'Es su trabajo, su app, su negocio' : 'It\'s their work, their app, their business'}</span>
                  </p>
                  <p className="flex items-start gap-2">
                    <span className="text-yellow-400">2.</span>
                    <span><strong className="text-zinc-200">{isSpanish ? 'Sería insostenible' : 'It would be unsustainable'}</strong> - {isSpanish ? 'Si DamyxIA pagara por cada usuario, quebrarías rápidamente' : 'If DamyxIA paid for each user, you\'d go bankrupt quickly'}</span>
                  </p>
                  <p className="flex items-start gap-2">
                    <span className="text-yellow-400">3.</span>
                    <span><strong className="text-zinc-200">{isSpanish ? 'Es el modelo estándar' : 'It\'s the standard model'}</strong> - {isSpanish ? 'Ninguna herramienta de desarrollo paga el hosting de sus usuarios' : 'No development tool pays for its users\' hosting'}</span>
                  </p>
                </div>
              </div>
              
              {/* Free Alternatives */}
              <div className="bg-green-600/10 border border-green-500/30 rounded-xl p-6">
                <h4 className="font-semibold text-green-400 mb-4 flex items-center gap-2">
                  🆓 {isSpanish ? 'Alternativas GRATUITAS para empezar:' : 'FREE alternatives to start:'}
                </h4>
                <div className="grid md:grid-cols-2 gap-4 text-sm">
                  <div className="bg-zinc-900/50 rounded-lg p-3">
                    <p className="text-zinc-200 font-medium">Vercel</p>
                    <p className="text-zinc-400">{isSpanish ? 'Hosting gratis para sitios web' : 'Free hosting for websites'}</p>
                  </div>
                  <div className="bg-zinc-900/50 rounded-lg p-3">
                    <p className="text-zinc-200 font-medium">Netlify</p>
                    <p className="text-zinc-400">{isSpanish ? 'Hosting gratis con dominio .netlify.app' : 'Free hosting with .netlify.app domain'}</p>
                  </div>
                  <div className="bg-zinc-900/50 rounded-lg p-3">
                    <p className="text-zinc-200 font-medium">GitHub Pages</p>
                    <p className="text-zinc-400">{isSpanish ? 'Sitios estáticos gratuitos' : 'Free static sites'}</p>
                  </div>
                  <div className="bg-zinc-900/50 rounded-lg p-3">
                    <p className="text-zinc-200 font-medium">Firebase Hosting</p>
                    <p className="text-zinc-400">{isSpanish ? 'Tier gratuito generoso' : 'Generous free tier'}</p>
                  </div>
                  <div className="bg-zinc-900/50 rounded-lg p-3">
                    <p className="text-zinc-200 font-medium">Railway</p>
                    <p className="text-zinc-400">{isSpanish ? '$5/mes de crédito gratis' : '$5/month free credit'}</p>
                  </div>
                  <div className="bg-zinc-900/50 rounded-lg p-3">
                    <p className="text-zinc-200 font-medium">Render</p>
                    <p className="text-zinc-400">{isSpanish ? 'Backend y bases de datos gratis' : 'Free backend and databases'}</p>
                  </div>
                </div>
              </div>
            </div>
          )}
          
          {/* Guides Tab */}
          {activeTab === 'guides' && (
            <div className="space-y-4">
              <p className="text-zinc-400 mb-6">
                {isSpanish 
                  ? 'Guías paso a paso para publicar tu proyecto en diferentes plataformas:'
                  : 'Step-by-step guides to publish your project on different platforms:'}
              </p>
              
              {/* Web Hosting Guide */}
              <div className="bg-zinc-800/50 rounded-xl overflow-hidden">
                <button
                  onClick={() => setExpandedGuide(expandedGuide === 'web' ? null : 'web')}
                  className="w-full px-4 py-4 flex items-center justify-between hover:bg-zinc-700/50 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <Globe className="w-6 h-6 text-blue-400" />
                    <div className="text-left">
                      <h4 className="font-semibold text-zinc-200">{isSpanish ? 'Sitios Web y Aplicaciones Web' : 'Websites & Web Applications'}</h4>
                      <p className="text-sm text-zinc-400">Vercel, Netlify, AWS, Google Cloud</p>
                    </div>
                  </div>
                  {expandedGuide === 'web' ? <ChevronUp className="w-5 h-5 text-zinc-400" /> : <ChevronDown className="w-5 h-5 text-zinc-400" />}
                </button>
                
                {expandedGuide === 'web' && (
                  <div className="px-4 pb-4 space-y-4">
                    <div className="bg-zinc-900/50 rounded-lg p-4">
                      <h5 className="font-medium text-green-400 mb-3 flex items-center gap-2">
                        <Rocket className="w-4 h-4" />
                        Vercel ({isSpanish ? 'Más fácil - Recomendado' : 'Easiest - Recommended'})
                      </h5>
                      <ol className="space-y-2 text-sm text-zinc-300">
                        <li className="flex items-start gap-2">
                          <span className="text-indigo-400 font-bold">1.</span>
                          {isSpanish ? 'Descarga tu código de DamyxIA' : 'Download your code from DamyxIA'}
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="text-indigo-400 font-bold">2.</span>
                          {isSpanish ? 'Crea una cuenta en' : 'Create an account at'} <a href="https://vercel.com" target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:underline">vercel.com</a>
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="text-indigo-400 font-bold">3.</span>
                          {isSpanish ? 'Sube tu código a GitHub' : 'Upload your code to GitHub'}
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="text-indigo-400 font-bold">4.</span>
                          {isSpanish ? 'Conecta tu repositorio con Vercel' : 'Connect your repository to Vercel'}
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="text-indigo-400 font-bold">5.</span>
                          {isSpanish ? '¡Listo! Tu sitio estará en tu-proyecto.vercel.app' : 'Done! Your site will be at your-project.vercel.app'}
                        </li>
                      </ol>
                      <div className="mt-3 p-2 bg-zinc-800 rounded text-xs font-mono text-zinc-400">
                        <div className="flex items-center justify-between">
                          <span>{isSpanish ? 'Costo: GRATIS' : 'Cost: FREE'}</span>
                          <span className="text-green-400">$0</span>
                        </div>
                      </div>
                    </div>
                    
                    <div className="bg-zinc-900/50 rounded-lg p-4">
                      <h5 className="font-medium text-orange-400 mb-3 flex items-center gap-2">
                        <Cloud className="w-4 h-4" />
                        AWS / Google Cloud / Azure ({isSpanish ? 'Avanzado' : 'Advanced'})
                      </h5>
                      <ol className="space-y-2 text-sm text-zinc-300">
                        <li className="flex items-start gap-2">
                          <span className="text-indigo-400 font-bold">1.</span>
                          {isSpanish ? 'Descarga tu código de DamyxIA' : 'Download your code from DamyxIA'}
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="text-indigo-400 font-bold">2.</span>
                          {isSpanish ? 'Crea una cuenta en el proveedor elegido' : 'Create an account with your chosen provider'}
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="text-indigo-400 font-bold">3.</span>
                          {isSpanish ? 'Configura un servidor (EC2, VM, etc.)' : 'Set up a server (EC2, VM, etc.)'}
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="text-indigo-400 font-bold">4.</span>
                          {isSpanish ? 'Sube tu código y configura el entorno' : 'Upload your code and configure environment'}
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="text-indigo-400 font-bold">5.</span>
                          {isSpanish ? 'Configura dominio y SSL' : 'Set up domain and SSL'}
                        </li>
                      </ol>
                      <div className="mt-3 p-2 bg-zinc-800 rounded text-xs font-mono text-zinc-400">
                        <div className="flex items-center justify-between">
                          <span>{isSpanish ? 'Costo: Variable' : 'Cost: Variable'}</span>
                          <span className="text-yellow-400">$5-100+/{isSpanish ? 'mes' : 'month'}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
              
              {/* Android Guide */}
              <div className="bg-zinc-800/50 rounded-xl overflow-hidden">
                <button
                  onClick={() => setExpandedGuide(expandedGuide === 'android' ? null : 'android')}
                  className="w-full px-4 py-4 flex items-center justify-between hover:bg-zinc-700/50 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <Play className="w-6 h-6 text-green-500" />
                    <div className="text-left">
                      <h4 className="font-semibold text-zinc-200">Google Play Store (Android)</h4>
                      <p className="text-sm text-zinc-400">{isSpanish ? 'Publicar apps Android' : 'Publish Android apps'}</p>
                    </div>
                  </div>
                  {expandedGuide === 'android' ? <ChevronUp className="w-5 h-5 text-zinc-400" /> : <ChevronDown className="w-5 h-5 text-zinc-400" />}
                </button>
                
                {expandedGuide === 'android' && (
                  <div className="px-4 pb-4 space-y-4">
                    <div className="bg-zinc-900/50 rounded-lg p-4">
                      <h5 className="font-medium text-green-400 mb-3">{isSpanish ? 'Requisitos:' : 'Requirements:'}</h5>
                      <ul className="space-y-1 text-sm text-zinc-300">
                        <li>• {isSpanish ? 'Cuenta de Google Play Console' : 'Google Play Console account'} (<span className="text-yellow-400">$25 {isSpanish ? 'una vez' : 'one-time'}</span>)</li>
                        <li>• {isSpanish ? 'Código de app móvil (React Native, Flutter)' : 'Mobile app code (React Native, Flutter)'}</li>
                        <li>• {isSpanish ? 'Computadora con Node.js o Flutter instalado' : 'Computer with Node.js or Flutter installed'}</li>
                      </ul>
                    </div>
                    
                    <div className="bg-zinc-900/50 rounded-lg p-4">
                      <h5 className="font-medium text-indigo-400 mb-3">{isSpanish ? 'Pasos:' : 'Steps:'}</h5>
                      <ol className="space-y-2 text-sm text-zinc-300">
                        <li className="flex items-start gap-2">
                          <span className="text-indigo-400 font-bold">1.</span>
                          {isSpanish ? 'Descarga tu código de DamyxIA' : 'Download your code from DamyxIA'}
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="text-indigo-400 font-bold">2.</span>
                          {isSpanish ? 'Registra una cuenta en' : 'Register an account at'} <a href="https://play.google.com/console" target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:underline">play.google.com/console</a>
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="text-indigo-400 font-bold">3.</span>
                          {isSpanish ? 'Paga la tarifa única de $25' : 'Pay the one-time $25 fee'}
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="text-indigo-400 font-bold">4.</span>
                          {isSpanish ? 'Compila tu app: ' : 'Build your app: '}
                          <code className="px-1 bg-zinc-800 rounded text-xs">npx react-native build-android --mode=release</code>
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="text-indigo-400 font-bold">5.</span>
                          {isSpanish ? 'Sube el archivo .aab a Play Console' : 'Upload the .aab file to Play Console'}
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="text-indigo-400 font-bold">6.</span>
                          {isSpanish ? 'Completa la ficha de la app (capturas, descripción)' : 'Complete the app listing (screenshots, description)'}
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="text-indigo-400 font-bold">7.</span>
                          {isSpanish ? 'Envía para revisión (1-3 días)' : 'Submit for review (1-3 days)'}
                        </li>
                      </ol>
                    </div>
                    
                    <div className="mt-3 p-3 bg-green-600/10 border border-green-500/30 rounded-lg text-sm">
                      <p className="text-green-400 font-medium">{isSpanish ? '💰 Costo total:' : '💰 Total cost:'}</p>
                      <p className="text-zinc-300">$25 USD ({isSpanish ? 'pago único, de por vida' : 'one-time payment, lifetime'})</p>
                    </div>
                  </div>
                )}
              </div>
              
              {/* iOS Guide */}
              <div className="bg-zinc-800/50 rounded-xl overflow-hidden">
                <button
                  onClick={() => setExpandedGuide(expandedGuide === 'ios' ? null : 'ios')}
                  className="w-full px-4 py-4 flex items-center justify-between hover:bg-zinc-700/50 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <Apple className="w-6 h-6 text-zinc-300" />
                    <div className="text-left">
                      <h4 className="font-semibold text-zinc-200">Apple App Store (iOS)</h4>
                      <p className="text-sm text-zinc-400">{isSpanish ? 'Publicar apps iPhone/iPad' : 'Publish iPhone/iPad apps'}</p>
                    </div>
                  </div>
                  {expandedGuide === 'ios' ? <ChevronUp className="w-5 h-5 text-zinc-400" /> : <ChevronDown className="w-5 h-5 text-zinc-400" />}
                </button>
                
                {expandedGuide === 'ios' && (
                  <div className="px-4 pb-4 space-y-4">
                    <div className="bg-zinc-900/50 rounded-lg p-4">
                      <h5 className="font-medium text-red-400 mb-3">{isSpanish ? '⚠️ Requisitos:' : '⚠️ Requirements:'}</h5>
                      <ul className="space-y-1 text-sm text-zinc-300">
                        <li>• {isSpanish ? 'Cuenta de Apple Developer' : 'Apple Developer account'} (<span className="text-yellow-400">$99/{isSpanish ? 'año' : 'year'}</span>)</li>
                        <li>• <strong className="text-orange-400">{isSpanish ? 'Mac con macOS' : 'Mac with macOS'}</strong> ({isSpanish ? '¡Obligatorio!)' : '(Required!)'}</li>
                        <li>• Xcode ({isSpanish ? 'gratis en App Store' : 'free on App Store'})</li>
                        <li>• {isSpanish ? 'Código de app móvil (React Native, Flutter)' : 'Mobile app code (React Native, Flutter)'}</li>
                      </ul>
                    </div>
                    
                    <div className="bg-zinc-900/50 rounded-lg p-4">
                      <h5 className="font-medium text-indigo-400 mb-3">{isSpanish ? 'Pasos:' : 'Steps:'}</h5>
                      <ol className="space-y-2 text-sm text-zinc-300">
                        <li className="flex items-start gap-2">
                          <span className="text-indigo-400 font-bold">1.</span>
                          {isSpanish ? 'Descarga tu código de DamyxIA' : 'Download your code from DamyxIA'}
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="text-indigo-400 font-bold">2.</span>
                          {isSpanish ? 'Registra una cuenta en' : 'Register an account at'} <a href="https://developer.apple.com" target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:underline">developer.apple.com</a>
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="text-indigo-400 font-bold">3.</span>
                          {isSpanish ? 'Paga la membresía anual de $99' : 'Pay the $99 annual membership'}
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="text-indigo-400 font-bold">4.</span>
                          {isSpanish ? 'Abre el proyecto en Xcode (Mac)' : 'Open the project in Xcode (Mac)'}
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="text-indigo-400 font-bold">5.</span>
                          {isSpanish ? 'Configura certificados y provisioning profiles' : 'Set up certificates and provisioning profiles'}
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="text-indigo-400 font-bold">6.</span>
                          {isSpanish ? 'Compila y sube a App Store Connect' : 'Build and upload to App Store Connect'}
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="text-indigo-400 font-bold">7.</span>
                          {isSpanish ? 'Completa la ficha y envía para revisión (1-7 días)' : 'Complete listing and submit for review (1-7 days)'}
                        </li>
                      </ol>
                    </div>
                    
                    <div className="mt-3 p-3 bg-yellow-600/10 border border-yellow-500/30 rounded-lg text-sm">
                      <p className="text-yellow-400 font-medium">{isSpanish ? '💰 Costo anual:' : '💰 Annual cost:'}</p>
                      <p className="text-zinc-300">$99 USD/{isSpanish ? 'año' : 'year'} ({isSpanish ? 'se renueva cada año' : 'renews every year'})</p>
                    </div>
                    
                    <div className="mt-2 p-3 bg-orange-600/10 border border-orange-500/30 rounded-lg text-sm">
                      <p className="text-orange-400 font-medium">💻 {isSpanish ? '¿No tienes Mac?' : 'Don\'t have a Mac?'}</p>
                      <p className="text-zinc-300">{isSpanish 
                        ? 'Puedes usar servicios como MacStadium o alquilar una Mac en la nube, pero tienen costo adicional.'
                        : 'You can use services like MacStadium or rent a Mac in the cloud, but they have additional costs.'}</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
        
        {/* Footer */}
        <div className="p-4 border-t border-zinc-700 bg-zinc-800/50">
          <p className="text-center text-sm text-zinc-500">
            {isSpanish 
              ? '¿Tienes dudas? Pregunta a DamyxIA en el chat para ayuda personalizada.'
              : 'Have questions? Ask DamyxIA in the chat for personalized help.'}
          </p>
        </div>
      </div>
    </div>
  );
};

export default ServicesInfo;
